import React, { memo, useCallback, useMemo, useState } from "react";
// styles
import {
    CollapsedBillDetailDetailsContainer,
    Content,
    Heading,
    HeadingArrow,
    HeadingContainer,
    HeadingSubText,
    HeadingText,
    Separator,
    HeadingAmount,
    HeadingArrowContainer,
    HeadingSubTextUpsellCollapseWrapper,
    HeadingSubTextUpsell,
    ChevronRight,
    AmountContainer,
    IconWrapper,
    TooltipIconWrapper,
    TextWrapperCentreAlign,
} from "./styles";

// types
import { CollapsableBillDetailsVariants, ICollapsedBillDetails } from "@swiggy-private/cart-types";

// test ids
import { COLLAPSABLE_BILL_DETAILS_TEST_IDS } from "@swiggy-private/cart-test-ids";

// helpers
import {
    getCollapsableBillDetailsA11yText,
    getCollpasableBillDetailsAnalyticsPayload,
} from "@core-ui/helpers/BillDetailsHelper";
import {  useEffectOnce } from "@core-ui/hooks/UseEffectOnce";
import { replaceText } from "@core-ui/helpers/HtmlFormatter";
import { Image } from "@swiggy-private/dls-web-core/dist/components/Image";
import { IColors } from "@swiggy-private/dls-web-common/dist/components/types";
import { isEmpty } from "@swiggy-private/js-utils";

// constants
import {
    BILL_ICON,
    BILL_ICON_SIZE,
    BILL_UPSELL_ICON_SIZE,
    COLLAPSIBLE_BILL_TOOL_TIP_IMG_CLASS,
} from "@core-ui/constants/BillDetails";

export interface ICollapsableBillDetailsWrapperProps {
    collapsedBillDetails?: ICollapsedBillDetails;
    children: React.ReactNode;
    collapseBillDetailXPVariant?: CollapsableBillDetailsVariants;
    isBillDetailSectionUncollapsed?: boolean;
    doNotShowCollapsableBill?: boolean;
    onImpressionBillViewChevron?: (context: Record<string, unknown>) => void;
    onClickBillViewChevron?: (context: Record<string, unknown>) => void;
    setBillDetailSectionUncollapsed: (value: boolean) => void;
    onImpressionCollapsibleBillUpsell: (upsellMessage: string) => void;
}

const CollapsableBillDetailsWrapper = (props: ICollapsableBillDetailsWrapperProps) => {
    const {
        collapsedBillDetails,
        isBillDetailSectionUncollapsed,
        doNotShowCollapsableBill,
        setBillDetailSectionUncollapsed,
    } = props;

    const [isExpanded, setIsExpanded] = useState<boolean>(isBillDetailSectionUncollapsed || false);
    const [rotation, setRotation] = useState(isExpanded ? 180 : 0);

    const handleToggle = useCallback(() => {
        props.onClickBillViewChevron?.(getCollpasableBillDetailsAnalyticsPayload(collapsedBillDetails, !isExpanded));
        setBillDetailSectionUncollapsed(!isExpanded);
        setIsExpanded(!isExpanded);
        setRotation(rotation + 180);
    }, [collapsedBillDetails, isExpanded, props, rotation, setBillDetailSectionUncollapsed]);

    const getSubText = useMemo(() => {
        switch (props.collapseBillDetailXPVariant) {
            case CollapsableBillDetailsVariants.VARIANT_WITH_SAVINGS:
            case CollapsableBillDetailsVariants.VARIANT_WITH_SAVING_UPSELL:
                if (!isEmpty(collapsedBillDetails?.discountMessage)) {
                    return collapsedBillDetails?.discountMessage;
                }
                return collapsedBillDetails?.subtitle;
            case CollapsableBillDetailsVariants.VARIANT_WITHOUT_SAVING_UPSELL:
                return collapsedBillDetails?.subtitle;
            default:
                return collapsedBillDetails?.subtitle;
        }
    }, [collapsedBillDetails?.discountMessage, collapsedBillDetails?.subtitle, props.collapseBillDetailXPVariant]);

    const showUpsellTooltip = useMemo(
        () =>
            !isEmpty(props.collapsedBillDetails?.upsellMessage) &&
            (props.collapseBillDetailXPVariant === CollapsableBillDetailsVariants.VARIANT_WITH_UPSELL ||
                props.collapseBillDetailXPVariant === CollapsableBillDetailsVariants.VARIANT_WITH_SAVING_UPSELL),
        [props.collapseBillDetailXPVariant, props.collapsedBillDetails?.upsellMessage],
    );

    const isSavingCallout = useMemo(() => {
        return (
            !isEmpty(props.collapsedBillDetails?.discountMessage) &&
            (props.collapseBillDetailXPVariant === CollapsableBillDetailsVariants.VARIANT_WITH_SAVINGS ||
                props.collapseBillDetailXPVariant === CollapsableBillDetailsVariants.VARIANT_WITH_SAVING_UPSELL)
        );
    }, [props.collapseBillDetailXPVariant, props.collapsedBillDetails?.discountMessage]);

    const getHeadingSubTextColor = useMemo(() => {
        const color = collapsedBillDetails?.discountMessageColor;
        if (color) {
            return color;
        }
        return isSavingCallout ? "special_positive" : "text_Low_Emphasis";
    }, [collapsedBillDetails?.discountMessageColor, isSavingCallout]);

    const getA11yText = useMemo(() => {
        return getCollapsableBillDetailsA11yText(collapsedBillDetails, getSubText);
    }, [collapsedBillDetails, getSubText]);

    const showStrikoffAmount = useMemo(() => {
        return !isEmpty(collapsedBillDetails?.totalAmount);
    }, [collapsedBillDetails?.totalAmount]);

    useEffectOnce(() => {
        if (!doNotShowCollapsableBill) {
            const savedMsg = collapsedBillDetails?.discountMessage?.split("{")[0];
            props.onImpressionBillViewChevron?.(
                getCollpasableBillDetailsAnalyticsPayload(collapsedBillDetails, isExpanded, savedMsg),
            );

            if (showUpsellTooltip) {
                props.onImpressionCollapsibleBillUpsell(props.collapsedBillDetails?.upsellMessage || "");
            }
        }
    });

    if (doNotShowCollapsableBill) {
        return <>{props.children}</>;
    }

    return (
        <>
            <CollapsedBillDetailDetailsContainer
                onClick={handleToggle}
                tabIndex={0}
                aria-expanded={isExpanded}
            >
                <Heading>
                    <HeadingContainer>
                        <IconWrapper>
                            <Image
                                cloudinaryId={collapsedBillDetails?.icon || BILL_ICON}
                                alt=""
                                width={BILL_ICON_SIZE}
                                height={BILL_ICON_SIZE}
                            />
                        </IconWrapper>
                        <div>
                            <AmountContainer>
                                <HeadingText
                                    aria-label={getA11yText}
                                    variant="V2.body2.neutral.medium"
                                    color="text_High_Emphasis"
                                    strikedOff={showStrikoffAmount}
                                >
                                    {collapsedBillDetails?.title}{" "}
                                </HeadingText>
                                {showStrikoffAmount && (
                                    <HeadingAmount
                                        variant="V2.body2.neutral.medium"
                                        color="text_Med_Emphasis"
                                        strikedOff
                                        aria-hidden
                                    >
                                        {collapsedBillDetails?.totalAmount}
                                    </HeadingAmount>
                                )}
                                {!isEmpty(collapsedBillDetails?.strikeoffAmount) && (
                                    <HeadingAmount
                                        variant={"V2.body2.neutral.medium"}
                                        color="text_High_Emphasis"
                                        aria-hidden
                                    >
                                        {collapsedBillDetails?.strikeoffAmount}
                                    </HeadingAmount>
                                )}
                            </AmountContainer>
                            {!isEmpty(getSubText) && (
                                <HeadingSubText
                                    variant={"V2.body3.neutral.regular"}
                                    color={getHeadingSubTextColor as keyof IColors}
                                    aria-hidden
                                >
                                    <TextWrapperCentreAlign>{replaceText(getSubText, "", true)}</TextWrapperCentreAlign>
                                </HeadingSubText>
                            )}
                        </div>
                    </HeadingContainer>
                    <HeadingArrowContainer>
                        <HeadingArrow
                            data-testid={COLLAPSABLE_BILL_DETAILS_TEST_IDS.COLLAPSABLE_BILL_COLLAPSE_CTA}
                            isExpanded={!!isExpanded}
                            currentRotation={rotation}
                        >
                            <ChevronRight />
                        </HeadingArrow>
                    </HeadingArrowContainer>
                </Heading>
                {showUpsellTooltip && (
                    <HeadingSubTextUpsellCollapseWrapper isExpanded={isExpanded}>
                        <HeadingSubTextUpsell variant="V2.body3.neutral.regular" color="text_Med_Emphasis" aria-hidden>
                            {collapsedBillDetails?.upsellIcon && (
                                <TooltipIconWrapper>
                                    <Image
                                        height={BILL_UPSELL_ICON_SIZE}
                                        width={BILL_UPSELL_ICON_SIZE}
                                        cloudinaryId={collapsedBillDetails?.upsellIcon}
                                        alt=""
                                    />
                                </TooltipIconWrapper>
                            )}
                            {collapsedBillDetails?.upsellMessage &&
                                replaceText(collapsedBillDetails?.upsellMessage, COLLAPSIBLE_BILL_TOOL_TIP_IMG_CLASS)}
                        </HeadingSubTextUpsell>
                    </HeadingSubTextUpsellCollapseWrapper>
                )}
            </CollapsedBillDetailDetailsContainer>
            <Content
                data-testid={
                    isExpanded
                        ? COLLAPSABLE_BILL_DETAILS_TEST_IDS.COLLAPSABLE_BILL_EXPANDED_CONTENT
                        : COLLAPSABLE_BILL_DETAILS_TEST_IDS.COLLAPSABLE_BILL_COLLAPSED_CONTENT
                }
                isExpanded={!!isExpanded}
                aria-hidden={!isExpanded}
            >
                <Separator />
                {props.children}
            </Content>
        </>
    );
};

export default memo(CollapsableBillDetailsWrapper);
